import type { GuideDto, GuideHighlight, GuideSummaryDto } from '@edms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';
import { Empty, formatDate, useToast } from '../lib/ui';

/** Текст шага: абзацы через пустую строку, **жирный** — в двойных звёздочках. */
function StepText({ text }: { text: string }) {
  if (!text) return null;
  return (
    <>
      {text.split(/\n\s*\n/).map((para, i) => (
        <p key={i}>
          {para.split('**').map((part, j) => (j % 2 ? <b key={j}>{part}</b> : <Fragment key={j}>{part}</Fragment>))}
        </p>
      ))}
    </>
  );
}

/** Скриншот с подсвеченной областью; по нажатию — во весь экран. */
function Shot({ src, highlight, alt }: { src: string; highlight: GuideHighlight | null; alt: string }) {
  const [zoom, setZoom] = useState(false);
  const frame = (h: GuideHighlight | null) =>
    h && <span className="guide-hl" style={{ left: `${h.x * 100}%`, top: `${h.y * 100}%`, width: `${h.w * 100}%`, height: `${h.h * 100}%` }} aria-hidden />;
  return (
    <>
      <button type="button" className="guide-shot" onClick={() => setZoom(true)} title="Открыть во весь экран">
        <img src={src} alt={alt} loading="lazy" />
        {frame(highlight)}
      </button>
      {zoom && (
        <div className="guide-zoom" role="dialog" aria-label={alt} onClick={() => setZoom(false)}>
          <div className="guide-zoom-inner">
            <img src={src} alt={alt} />
            {frame(highlight)}
          </div>
          <span className="guide-zoom-hint">Нажмите, чтобы закрыть</span>
        </div>
      )}
    </>
  );
}

function StepBody({ step, index }: { step: GuideDto['steps'][number]; index: number }) {
  return (
    <>
      <div className="guide-text">
        <StepText text={step.text} />
      </div>
      {step.image && <Shot src={step.image} highlight={step.highlight} alt={`Шаг ${index + 1}. ${step.title}`} />}
      {step.tip && (
        <div className="guide-tip">
          <b>Подсказка.</b> {step.tip}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Просмотр инструкции
// ---------------------------------------------------------------------------

export function GuideViewPage() {
  const { key = '' } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const guide = useQuery({ queryKey: ['guides', key], queryFn: () => api.get<GuideDto>(`/guides/${key}`) });
  const all = params.get('mode') === 'all';
  const total = guide.data?.steps.length ?? 0;
  const clamp = (n: number) => Math.min(Math.max(n || 1, 1), Math.max(total, 1));
  const urlStep = clamp(Number(params.get('step') ?? 1));
  // Шаг хранится в состоянии: быстрые нажатия ← → складываются, адрес лишь повторяет шаг.
  const [step, setStep] = useState(urlStep);
  const written = useRef(urlStep);
  const [seen, setSeen] = useState<Set<number>>(() => new Set([1]));
  const top = useRef<HTMLDivElement>(null);

  // Переход по адресу (кнопка «Назад» браузера, ссылка на шаг) — показываем этот шаг.
  useEffect(() => {
    if (urlStep !== written.current) {
      written.current = urlStep;
      setStep(urlStep);
    }
  }, [urlStep]);
  // Шаг → адрес: ссылкой на конкретный шаг можно поделиться.
  useEffect(() => {
    if (step === written.current) return;
    written.current = step;
    setParams((p) => {
      const next = new URLSearchParams(p);
      next.set('step', String(step));
      return next;
    });
  }, [step, setParams]);

  /** Переход на шаг: число — на него, { by } — на столько вперёд или назад от текущего. */
  const go = (to: number | { by: number }) => {
    setStep((cur) => clamp(typeof to === 'number' ? to : cur + to.by));
    top.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  useEffect(() => setSeen((s) => (s.has(step) ? s : new Set(s).add(step))), [step]);
  const setMode = (m: 'steps' | 'all') =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      if (m === 'all') next.set('mode', 'all');
      else next.delete('mode');
      return next;
    });

  // ← и → листают шаги.
  useEffect(() => {
    if (all || !total) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'ArrowRight') go({ by: 1 });
      if (e.key === 'ArrowLeft') go({ by: -1 });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (guide.isError) {
    return (
      <div className="card">
        <Empty emoji="📘" title="Инструкция не найдена">
          <Link to="/guides">Все инструкции</Link>
        </Empty>
      </div>
    );
  }
  const g = guide.data;
  if (!g) return <div className="faint">Загрузка…</div>;
  const current = g.steps[step - 1]!;

  return (
    <div ref={top}>
      <div className="page-head">
        <div>
          <Link to="/guides" className="faint">
            ← Все инструкции
          </Link>
          <h1>
            <span aria-hidden>{g.icon}</span> {g.title}
          </h1>
          {g.description && <p className="muted">{g.description}</p>}
        </div>
        <a className="btn" href={`/api/guides/${g.key}/pdf`} download>
          <Icon name="download" size={18} /> Скачать PDF
        </a>
      </div>
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={!all} className={`tab${!all ? ' active' : ''}`} onClick={() => setMode('steps')}>
          Пошагово
        </button>
        <button role="tab" aria-selected={all} className={`tab${all ? ' active' : ''}`} onClick={() => setMode('all')}>
          Целиком
        </button>
      </div>

      {all ? (
        <div className="guide-all">
          <nav className="card card-pad guide-toc" aria-label="Содержание">
            <b>Содержание</b>
            <ol>
              {g.steps.map((s, i) => (
                <li key={i}>
                  <a href={`#step-${i + 1}`}>{s.title}</a>
                </li>
              ))}
            </ol>
          </nav>
          {g.steps.map((s, i) => (
            <section key={i} id={`step-${i + 1}`} className="card card-pad guide-step">
              <div className="guide-step-num">
                Шаг {i + 1} из {total}
              </div>
              <h2>{s.title}</h2>
              <StepBody step={s} index={i} />
            </section>
          ))}
        </div>
      ) : (
        <div className="guide-layout">
          <ol className="guide-steps card" aria-label="Шаги">
            {g.steps.map((s, i) => (
              <li key={i}>
                <button type="button" className={`guide-steps-item${i + 1 === step ? ' current' : ''}${seen.has(i + 1) && i + 1 !== step ? ' seen' : ''}`} onClick={() => go(i + 1)}>
                  <span className="guide-steps-n">{seen.has(i + 1) && i + 1 !== step ? '✓' : i + 1}</span>
                  <span>{s.title}</span>
                </button>
              </li>
            ))}
          </ol>
          <section className="card card-pad guide-step">
            <div className="guide-progress" aria-hidden>
              <span style={{ width: `${(step / total) * 100}%` }} />
            </div>
            <div className="guide-step-num">
              Шаг {step} из {total}
            </div>
            <h2>{current.title}</h2>
            <StepBody step={current} index={step - 1} />
            <div className="guide-nav">
              <button type="button" className="btn" disabled={step === 1} onClick={() => go({ by: -1 })}>
                ← Назад
              </button>
              <span className="faint desktop-only">Листать можно стрелками ← →</span>
              {step < total ? (
                <button type="button" className="btn btn-primary" onClick={() => go({ by: 1 })}>
                  Далее →
                </button>
              ) : (
                <button type="button" className="btn btn-primary" onClick={() => void navigate('/guides')}>
                  Готово ✓
                </button>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Список инструкций
// ---------------------------------------------------------------------------

function GuideCard({ g, manage, onDelete }: { g: GuideSummaryDto; manage: boolean; onDelete: () => void }) {
  return (
    <article className="card guide-card">
      <Link to={`/guides/${g.key}`} className="guide-card-main">
        <span className="guide-card-icon" aria-hidden>
          {g.icon}
        </span>
        <b>{g.title}</b>
        {g.description && <span className="faint">{g.description}</span>}
      </Link>
      <div className="guide-card-foot">
        <span className="faint">
          {g.steps} {g.steps % 10 === 1 && g.steps % 100 !== 11 ? 'шаг' : g.steps % 10 >= 2 && g.steps % 10 <= 4 && (g.steps % 100 < 10 || g.steps % 100 >= 20) ? 'шага' : 'шагов'} · {formatDate(g.updatedAt)}
          {manage && ` · версия ${g.version}`}
        </span>
        <span style={{ display: 'flex', gap: 6 }}>
          <Link className="btn btn-sm btn-primary" to={`/guides/${g.key}`}>
            Открыть
          </Link>
          <a className="btn btn-sm" href={`/api/guides/${g.key}/pdf`} download>
            PDF
          </a>
          {manage && (
            <button type="button" className="btn btn-sm btn-ghost" style={{ color: 'var(--danger)' }} onClick={onDelete} aria-label={`Удалить «${g.title}»`}>
              ×
            </button>
          )}
        </span>
      </div>
    </article>
  );
}

/** Для администратора: как подготовить инструкции и загрузка пакетов. */
function ManagePanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const uploadFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    for (const file of Array.from(files)) {
      const form = new FormData();
      form.append('file', file);
      try {
        const g = await api.upload<GuideSummaryDto>('/guides', form);
        toast(g.version > 1 ? `Обновлена: «${g.title}» (версия ${g.version})` : `Загружена: «${g.title}»`);
      } catch (e) {
        toast(`${file.name}: ${(e as Error).message}`, { error: true });
      }
    }
    setBusy(false);
    if (input.current) input.current.value = '';
    void qc.invalidateQueries({ queryKey: ['guides'] });
  };
  return (
    <details className="card card-pad guide-manage">
      <summary>
        <b>Управление инструкциями</b> <span className="faint">— для администратора</span>
      </summary>
      <ol>
        <li>
          <b>Выгрузите настройки</b> — маршруты, типы заявок, шаблоны, отделы и сотрудников (только ФИО и должности, без почт и паролей).
        </li>
        <li>
          <b>Передайте архив</b> тому, кто готовит инструкции: по нему снимаются скриншоты с вашими настройками.
        </li>
        <li>
          <b>Загрузите полученные пакеты</b> (.zip). Пакет с тем же ключом заменяет прежнюю версию инструкции.
        </li>
      </ol>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <a className="btn" href="/api/guides/export" download>
          <Icon name="download" size={18} /> Выгрузить настройки
        </a>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => input.current?.click()}>
          <Icon name="plus" size={18} /> {busy ? 'Загрузка…' : 'Загрузить инструкции (.zip)'}
        </button>
        <input ref={input} type="file" accept=".zip,application/zip" multiple hidden onChange={(e) => void uploadFiles(e.target.files)} />
      </div>
    </details>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ display: 'grid', gap: 10 }}>
      <h2 className="guide-section-title">{title}</h2>
      <div className="guide-grid">{children}</div>
    </section>
  );
}

export function GuidesPage() {
  const { can } = useAuth();
  const manage = can('guides.manage');
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({ queryKey: ['guides'], queryFn: () => api.get<GuideSummaryDto[]>('/guides') });
  const [q, setQ] = useState('');
  const remove = useMutation({
    mutationFn: (key: string) => api.delete(`/guides/${key}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['guides'] });
      toast('Инструкция удалена');
    },
    onError: (e) => toast(e.message, { error: true }),
  });
  const found = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (list.data ?? []).filter((g) => !s || `${g.title} ${g.description}`.toLowerCase().includes(s));
  }, [list.data, q]);
  const everyone = found.filter((g) => g.audience === 'all');
  const admins = found.filter((g) => g.audience !== 'all');
  const card = (g: GuideSummaryDto) => (
    <GuideCard key={g.key} g={g} manage={manage} onDelete={() => confirm(`Удалить инструкцию «${g.title}»?`) && remove.mutate(g.key)} />
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Инструкции</h1>
          <p className="muted">Пошаговые инструкции по работе с системой — с вашими маршрутами, типами заявок и сотрудниками. Смотрите на сайте или скачивайте PDF.</p>
        </div>
      </div>
      <div style={{ display: 'grid', gap: 18 }}>
        {manage && <ManagePanel />}
        {(list.data?.length ?? 0) > 3 && <input className="input" style={{ maxWidth: 420 }} placeholder="Поиск по инструкциям" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Поиск" />}
        {list.data?.length === 0 && (
          <div className="card">
            <Empty emoji="📘" title="Инструкций пока нет">
              {manage ? 'Загрузите пакеты инструкций в «Управление инструкциями»' : 'Скоро здесь появятся инструкции по работе с системой'}
            </Empty>
          </div>
        )}
        {everyone.length > 0 && <Section title="Для всех сотрудников">{everyone.map(card)}</Section>}
        {admins.length > 0 && <Section title="Для администраторов">{admins.map(card)}</Section>}
        {list.data && list.data.length > 0 && found.length === 0 && <p className="faint">Ничего не найдено</p>}
      </div>
    </>
  );
}
